import { Save } from "lucide-react";
import { useEffect, useState } from "react";
import { AdminApi, ApiError, agentIdentifier, localized } from "@/api";
import { Button, Checkbox, FileButton, Section, SelectField, TextField, TextareaField, ToggleField } from "@/components/ui";
import type { AgentFile, AgentState, SkillCatalog } from "@/types";
import { formatBytes } from "@/utils/format";
import { parseJson, prettyJson } from "@/utils/json";
import { PREPARED_SESSION_SPEC } from "@/components/prompt-lab/promptTargets";
import { PromptVersionsPanel } from "@/components/prompt-lab/PromptVersionsPanel";
import { FileUploadPanel } from "./FileUploadPanel";

/**
 * SAMS has no agent file store configured: without `SAMS_AGENT_ASSET_*` the
 * file routes are not mounted (FastAPI's bare 404 `Not Found`, no code), or
 * they answer 503 ASSET_STORAGE_UNAVAILABLE. An unknown agent is a coded 404
 * (SYNAPSE_AGENT_NOT_FOUND) and stays an error.
 */
function fileStoreMissing(error: unknown) {
  if (!(error instanceof ApiError)) return false;
  return (error.status === 404 && !error.code) || (error.status === 503 && error.code === "ASSET_STORAGE_UNAVAILABLE");
}

export function AgentEditor(props: {
  api: AdminApi;
  agent: AgentState;
  skillCatalog: SkillCatalog | null;
  saving: boolean;
  setSaving: (value: boolean) => void;
  onSaved: (agent: AgentState) => void;
  onError: (error: unknown) => void;
  onFlash?: (message: string) => void;
}) {
  const { api, agent, skillCatalog, saving, setSaving, onSaved, onError } = props;
  const onFlash = props.onFlash ?? (() => undefined);
  const id = agentIdentifier(agent);
  const template = agent.template;
  const [titleKo, setTitleKo] = useState(String(template.title.ko || ""));
  const [titleEn, setTitleEn] = useState(String(template.title.en || ""));
  const [phraseKo, setPhraseKo] = useState(String(template.phrase.ko || ""));
  const [phraseEn, setPhraseEn] = useState(String(template.phrase.en || ""));
  const [descriptionKo, setDescriptionKo] = useState(String(template.description.ko || ""));
  const [descriptionEn, setDescriptionEn] = useState(String(template.description.en || ""));
  const [sessionType, setSessionType] = useState(template.session_type || "direct");
  const [route, setRoute] = useState(template.route || "direct_chat");
  const [sortOrder, setSortOrder] = useState(String(template.sort_order || 0));
  const [active, setActive] = useState(template.is_active);
  const [configurationSchema, setConfigurationSchema] = useState(
    prettyJson(template.configuration_schema),
  );
  const [avatarAsset, setAvatarAsset] = useState(template.agent_avatar_asset_name || "");
  const [promptLocale, setPromptLocale] = useState(agent.prompt?.locale || "any");
  const [capabilities, setCapabilities] = useState(template.capabilities.join(", "));
  const [skillConfig, setSkillConfig] = useState(prettyJson(agent.skill_config));
  const [files, setFiles] = useState<AgentFile[]>([]);
  const [storeMissing, setStoreMissing] = useState(false);
  const [filePreview, setFilePreview] = useState("");

  const selectedSkills = new Set(
    capabilities
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean),
  );

  const save = async (fn: () => Promise<AgentState>) => {
    setSaving(true);
    try {
      onSaved(await fn());
    } catch (err) {
      onError(err);
    } finally {
      setSaving(false);
    }
  };

  const loadFiles = async () => {
    try {
      setFiles(await api.listFiles(id));
      setStoreMissing(false);
    } catch (err) {
      setFiles([]);
      if (fileStoreMissing(err)) setStoreMissing(true);
      else onError(err);
    }
  };

  useEffect(() => {
    void loadFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const toggleSkill = (name: string) => {
    const next = new Set(selectedSkills);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    setCapabilities(Array.from(next).sort().join(", "));
  };

  return (
    <div className="grid gap-4.5">
      <div className="flex items-start justify-between gap-5">
        <div>
          <h1 className="mb-1 text-[28px]">{localized(template.title, template.kind)}</h1>
          <p className="mb-4">{template.kind} · {template.session_type}</p>
        </div>
        <Button
          variant="outline"
          onClick={() => {
            if (window.confirm("이 에이전트의 저장된 설정을 prod에 바로 적용할까요?")) {
              void save(async () => {
                await api.promoteToProd(id);
                return agent;
              });
            }
          }}
          disabled={saving}
        >
          Prod에 적용
        </Button>
      </div>

      <Section title="Avatar">
        <div className="grid grid-cols-[132px_1fr] items-start gap-4.5">
          <img className="size-28 rounded-full border border-border bg-muted object-cover" src={api.absoluteUrl(template.agent_avatar_url)} alt="" />
          <div className="grid gap-3">
            <TextField label="Avatar asset name" value={avatarAsset} onChange={setAvatarAsset} />
            <div className="flex flex-wrap gap-2.5">
              <Button onClick={() => save(() => api.patchAvatar(id, { agent_avatar_asset_name: avatarAsset }))} disabled={saving}>
                <Save /> Save avatar
              </Button>
              <FileButton
                label="Upload / replace image"
                accept="image/*"
                onFile={(file) => save(() => api.uploadAvatar(id, file))}
              />
            </div>
          </div>
        </div>
      </Section>

      <Section title="Catalog" contentClassName="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <TextField label="Title ko" value={titleKo} onChange={setTitleKo} />
          <TextField label="Title en" value={titleEn} onChange={setTitleEn} />
          <TextField label="Phrase ko" value={phraseKo} onChange={setPhraseKo} />
          <TextField label="Phrase en" value={phraseEn} onChange={setPhraseEn} />
          <TextareaField label="Description ko" value={descriptionKo} onChange={setDescriptionKo} />
          <TextareaField label="Description en" value={descriptionEn} onChange={setDescriptionEn} />
          <SelectField label="Session type" value={sessionType} onChange={setSessionType} options={["direct", "group"]} />
          <SelectField label="Route" value={route} onChange={setRoute} options={["direct_chat", "group_chat"]} />
          <TextField label="Sort order" value={sortOrder} onChange={setSortOrder} />
          <ToggleField label="Active" checked={active} onChange={setActive} />
        </div>
        <TextareaField label="Configuration schema JSON" value={configurationSchema} onChange={setConfigurationSchema} rows={6} mono />
        <Button
          disabled={saving}
          onClick={() =>
            save(() =>
              api.patchCatalog(id, {
                title: { ko: titleKo, en: titleEn },
                phrase: { ko: phraseKo, en: phraseEn },
                description: { ko: descriptionKo, en: descriptionEn },
                session_type: sessionType,
                route,
                sort_order: Number(sortOrder) || 0,
                is_active: active,
                configuration_schema: parseJson(configurationSchema),
              }),
            )
          }
        >
          <Save /> Save catalog
        </Button>
      </Section>

      <Section title="Prompt" contentClassName="space-y-3">
        <SelectField
          label="Locale"
          value={promptLocale}
          onChange={setPromptLocale}
          options={[...new Set(["any", "ko", "en", promptLocale])]}
        />
        <PromptVersionsPanel
          api={api}
          scope={{ preparedKind: template.kind, locale: promptLocale }}
          spec={PREPARED_SESSION_SPEC}
          title={`Agent prompt (${promptLocale})`}
          onFlash={onFlash}
          onError={onError}
        />
      </Section>

      <Section title="Synapse Common Skills" contentClassName="space-y-3">
        <TextareaField label="Capabilities, comma-separated" value={capabilities} onChange={setCapabilities} rows={3} />
        <div className="max-h-110 overflow-auto rounded-lg border border-border bg-surface-input">
          {(skillCatalog?.skills || []).map((skill) => (
            <label key={skill.name} className="flex items-start gap-2.5 border-b border-border p-3 last:border-b-0">
              <Checkbox
                checked={selectedSkills.has(skill.name)}
                disabled={skill.required}
                onCheckedChange={() => toggleSkill(skill.name)}
              />
              <span className="grid gap-1">
                <strong>{skill.name}</strong>
                <small className="text-xs text-muted-foreground">{skill.super_category}{skill.category ? ` / ${skill.category}` : ""}</small>
                <small className="text-xs text-muted-foreground">{skill.description}</small>
              </span>
            </label>
          ))}
        </div>
        <TextareaField label="Skill config JSON" value={skillConfig} onChange={setSkillConfig} rows={6} mono />
        <Button
          disabled={saving}
          onClick={() =>
            save(() =>
              api.patchSkills(id, {
                capabilities: capabilities.split(",").map((item) => item.trim()).filter(Boolean),
                skill_config: parseJson(skillConfig),
              }),
            )
          }
        >
          <Save /> Save skills
        </Button>
      </Section>

      <Section title="Agent R2 Files" contentClassName="space-y-3">
        {storeMissing ? (
          <p role="status" className="rounded-lg border border-border p-3 text-sm text-muted-foreground">
            파일 저장소가 연결되지 않았습니다. SAMS 서버에 에이전트 파일 저장소 설정이 없어 파일을 보거나 올릴 수 없습니다.
          </p>
        ) : (
          <FileUploadPanel
            onUpload={(path, file, ingest) =>
              api.uploadFile(id, path, file, ingest).then(() => loadFiles())
            }
          />
        )}
        <div className="overflow-hidden rounded-lg border border-border">
          {files.map((file) => (
            <div className="flex items-center gap-2.5 border-b border-border px-3 py-2.5 last:border-b-0" key={file.path}>
              <span className="flex-1 break-all">{file.path}</span>
              <small className="text-xs text-muted-foreground">{formatBytes(file.size)}</small>
              <Button variant="ghost" onClick={() => api.readFile(id, file.path).then((content) => setFilePreview(content.content))}>
                View
              </Button>
              <Button variant="ghost" onClick={() => api.deleteFile(id, file.path).then(() => loadFiles())}>
                Delete
              </Button>
            </div>
          ))}
        </div>
        {filePreview && <pre className="block max-w-full overflow-x-hidden rounded-lg border border-border bg-surface-input p-3 font-mono whitespace-pre-wrap text-code-foreground wrap-anywhere">{filePreview}</pre>}
      </Section>
    </div>
  );
}
